const WHITE = "#ffffff";
const BLACK = "#000000";
const DEFAULT_THEME = "#000a60";
const DEFAULT_SIDEBAR_COLOR = "#ffffff";
const DEFAULT_INFO_COLOR = "#2bcfe0";
const DEFAULT_SUCCESS_COLOR = "#2b9e62";
const DEFAULT_WARNING_COLOR = "#fb6340";
const DEFAULT_DANGER_COLOR = "#ea2817";
const DEFAULT_SCOOTER_COLOR = "#2bcfe0";
const DEFAULT_BUTTON_STYLE = "standard";

function normalizeHex(color) {
  if (!color) {
    return null;
  }
  const value = String(color).trim();
  if (!value.startsWith("#")) {
    return null;
  }
  let hex = value.slice(1);
  if (hex.length === 3) {
    hex = hex.split("").map(char => char + char).join("");
  }
  if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }
  return `#${hex.toLowerCase()}`;
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex) || normalizeHex(DEFAULT_THEME);
  const int = parseInt(normalized.slice(1), 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

function relativeLuminance({ r, g, b }) {
  const toLinear = channel => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  );
}

/** True when the color reads as a light background (WCAG relative luminance). */
export function isLightColor(color) {
  return relativeLuminance(hexToRgb(color)) > 0.5;
}

/** True when a branding color is exactly white (#fff / #ffffff). */
export function isWhiteColor(color) {
  const normalized = normalizeHex(color);
  if (normalized === WHITE) {
    return true;
  }
  const raw = String(color || "").trim().toLowerCase();
  return raw === "white" || raw === "fff" || raw === "ffffff";
}

/** True only when primary branding is exactly white (#fff / #ffffff). */
export function isWhitePrimary(themeColor) {
  return isWhiteColor(themeColor);
}

const SEMANTIC_COLOR_DEFAULTS = {
  info: DEFAULT_INFO_COLOR,
  success: DEFAULT_SUCCESS_COLOR,
  warning: DEFAULT_WARNING_COLOR,
  danger: DEFAULT_DANGER_COLOR,
  scooter: DEFAULT_SCOOTER_COLOR,
};

export function getSemanticColorContrastVars(prefix, color) {
  const surface =
    normalizeHex(color) || normalizeHex(SEMANTIC_COLOR_DEFAULTS[prefix]) || color;
  const white = isWhiteColor(color);

  return {
    [`--branding-${prefix}-on-color`]: white ? BLACK : WHITE,
    [`--branding-${prefix}-border-color`]: white ? BLACK : surface,
    [`--branding-${prefix}-foreground-color`]: white ? BLACK : surface,
    [`--branding-${prefix}-alert-bg`]: white ? "rgba(0, 0, 0, 0.06)" : null,
  };
}

export function getAllSemanticContrastVars({
  infoColor,
  successColor,
  warningColor,
  dangerColor,
  scooterColor,
} = {}) {
  return {
    ...getSemanticColorContrastVars("info", infoColor),
    ...getSemanticColorContrastVars("success", successColor),
    ...getSemanticColorContrastVars("warning", warningColor),
    ...getSemanticColorContrastVars("danger", dangerColor),
    ...getSemanticColorContrastVars("scooter", scooterColor),
  };
}

function applyWhiteSemanticDataAttributes(element, colors = {}) {
  if (!element?.setAttribute) {
    return;
  }
  Object.entries({
    info: colors.info,
    success: colors.success,
    warning: colors.warning,
    danger: colors.danger,
    scooter: colors.scooter,
  }).forEach(([prefix, value]) => {
    const attr = `data-white-${prefix}`;
    if (isWhiteColor(value)) {
      element.setAttribute(attr, "true");
    } else {
      element.removeAttribute(attr);
    }
  });
}

/** Contrast tokens used by previews — only meaningful when primary is white. */
export function getThemeContrastColors(themeColor) {
  const whitePrimary = isWhitePrimary(themeColor);
  const theme = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  return {
    onTheme: whitePrimary ? BLACK : WHITE,
    accent: whitePrimary ? BLACK : theme,
    border: whitePrimary ? BLACK : theme,
  };
}

export function getThemeAlertContrastVars(themeColor) {
  const theme = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  const white = isWhiteColor(themeColor);
  return {
    "--branding-theme-foreground-color": white ? BLACK : theme,
    "--branding-theme-border-color": white ? BLACK : theme,
    "--branding-theme-alert-bg": white ? "rgba(0, 0, 0, 0.06)" : null,
  };
}

/**
 * Icon/text on a branded surface:
 * - Light background + different from theme → primary
 * - Dark/medium background → white (even when different from theme)
 * - Light background + same as theme → black
 */
export function getBrandingForegroundColor(backgroundColor, themeColor) {
  const primary = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  const backgroundNormalized = normalizeHex(backgroundColor) || primary;
  const backgroundIsLight = isLightColor(backgroundColor);
  const whitePrimary = isWhitePrimary(themeColor);

  if (backgroundNormalized !== primary) {
    if (whitePrimary && backgroundIsLight) {
      return BLACK;
    }
    return backgroundIsLight ? primary : WHITE;
  }

  return backgroundIsLight ? BLACK : WHITE;
}

/** @deprecated Use getBrandingForegroundColor */
export function getSidebarForegroundColor(sidebarColor, themeColor) {
  return getBrandingForegroundColor(sidebarColor, themeColor);
}

function surfaceTextVars(prefix, backgroundColor, themeColor) {
  const textColor = getBrandingForegroundColor(backgroundColor, themeColor);
  const { r, g, b } = hexToRgb(textColor);
  return {
    [`--branding-${prefix}-text-color`]: textColor,
    [`--branding-${prefix}-text-muted`]: `rgba(${r}, ${g}, ${b}, 0.92)`,
  };
}

/**
 * Sidebar + topbar CSS vars. Same rule on each surface, using that surface's background:
 * - Light + differs from theme → primary
 * - Dark/medium → white
 * - Light + same as theme → black
 * Hover: primary fill when sidebar differs from theme; subtle overlay when they match.
 */
export function getSidebarBrandingCssVars(sidebarColor, themeColor) {
  const primary = normalizeHex(themeColor) || normalizeHex(DEFAULT_THEME);
  const sidebarNormalized = normalizeHex(sidebarColor) || normalizeHex(DEFAULT_SIDEBAR_COLOR);
  const sidebarIsLight = isLightColor(sidebarColor);
  const sidebarDiffersFromTheme = sidebarNormalized !== primary;
  const sidebarTextColor = getBrandingForegroundColor(sidebarColor, themeColor);
  const whitePrimary = isWhitePrimary(themeColor);

  let hoverBg;
  let hoverBorder;
  let hoverTextColor;

  if (sidebarDiffersFromTheme) {
    if (whitePrimary) {
      hoverBg = "rgba(0, 0, 0, 0.08)";
      hoverBorder = "rgba(0, 0, 0, 0.18)";
      hoverTextColor = BLACK;
    } else {
      hoverBg = primary;
      hoverBorder = primary;
      hoverTextColor = WHITE;
    }
  } else {
    hoverBg = sidebarIsLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)";
    hoverBorder = sidebarIsLight ? "rgba(0, 0, 0, 0.18)" : "rgba(255, 255, 255, 0.18)";
    hoverTextColor = sidebarTextColor;
  }

  return {
    ...surfaceTextVars("sidebar", sidebarColor, themeColor),
    ...surfaceTextVars("topbar", themeColor, themeColor),
    "--branding-sidebar-hover-bg": hoverBg,
    "--branding-sidebar-hover-border": hoverBorder,
    "--branding-sidebar-hover-text-color": hoverTextColor,
  };
}

export function getButtonBorderRadius(buttonStyle) {
  const normalizedStyle = String(buttonStyle || "").trim().toLowerCase();
  if (normalizedStyle === "rounded") {
    return "999px";
  }
  if (normalizedStyle === "squared" || normalizedStyle === "square") {
    return "0";
  }
  return "4px";
}

/** Apply runtime branding CSS variables (inline, wins over iframe tenant defaults). */
export function applyBrandingCssVars(element, {
  themeColor,
  sidebarColor,
  infoColor,
  successColor,
  warningColor,
  dangerColor,
  scooterColor,
  buttonStyle,
} = {}) {
  if (!element?.style) {
    return;
  }
  const theme = normalizeHex(themeColor) || themeColor || DEFAULT_THEME;
  const sidebar = normalizeHex(sidebarColor) || sidebarColor || DEFAULT_SIDEBAR_COLOR;
  const info = normalizeHex(infoColor) || infoColor || DEFAULT_INFO_COLOR;
  const success = normalizeHex(successColor) || successColor || DEFAULT_SUCCESS_COLOR;
  const warning = normalizeHex(warningColor) || warningColor || DEFAULT_WARNING_COLOR;
  const danger = normalizeHex(dangerColor) || dangerColor || DEFAULT_DANGER_COLOR;
  const scooter = normalizeHex(scooterColor) || scooterColor || DEFAULT_SCOOTER_COLOR;
  const whitePrimary = isWhitePrimary(theme);

  if (element.setAttribute) {
    if (whitePrimary) {
      element.setAttribute("data-white-primary", "true");
    } else {
      element.removeAttribute("data-white-primary");
    }
  }

  applyWhiteSemanticDataAttributes(element, {
    info,
    success,
    warning,
    danger,
    scooter,
  });

  const vars = {
    "--branding-theme-color": theme,
    "--theme-color": theme,
    "--skin1": theme,
    "--branding-sidebar-color": sidebar,
    "--branding-info-color": info,
    "--branding-success-color": success,
    "--branding-warning-color": warning,
    "--branding-danger-color": danger,
    "--branding-scooter-color": scooter,
    "--branding-button-border-radius": getButtonBorderRadius(buttonStyle || DEFAULT_BUTTON_STYLE),
    ...getSidebarBrandingCssVars(sidebar, theme),
    ...getAllSemanticContrastVars({
      infoColor: info,
      successColor: success,
      warningColor: warning,
      dangerColor: danger,
      scooterColor: scooter,
    }),
    ...getThemeAlertContrastVars(theme),
  };
  Object.entries(vars).forEach(([name, value]) => {
    if (value != null) {
      element.style.setProperty(name, value);
    } else {
      element.style.removeProperty(name);
    }
  });
}
